import { ReactNode } from 'react'
import { useIntl } from 'react-intl'
import ChangeLine from '../../../components/novarium/ChangeLine'
import InfoTip from '../../../components/novarium/InfoTip'
import { formatShortDate, periodNeedsYear } from '../../../components/novarium/periodRange'
import { HospitalizationSummaryDTO } from '../../../dto/hospitalizationSummary/HospitalizationSummaryDTO'
import { percentVariation, pointsVariation, Variation } from '../../ServicesStatistics/statisticsChange'
import { parseIsoDate } from '../../ServicesStatistics/statisticsUtils'
import { formatCount, formatDecimal, formatPercent } from '../hospitalizationSummaryFormat'

interface IndicatorProps {
  label: string
  value: string
  /** Fórmula del indicador y qué valida el servicio (ⓘ, a demanda). */
  hint: string
  /** Renglones de detalle: el desglose y la variación contra el período anterior. */
  details?: ReactNode[]
}

function Indicator({ label, value, hint, details = [] }: IndicatorProps) {
  return (
    <div className="nv-card nv-hosp-ind">
      <dt className="nv-hosp-ind__label">
        {label}
        <InfoTip text={hint} />
      </dt>
      <dd className="nv-kpi">{value}</dd>
      {details.length === 0 && <dd className="nv-hosp-ind__detail">{' '}</dd>}
      {details.map((detail, index) => <dd key={index} className="nv-hosp-ind__detail">{detail}</dd>)}
    </div>
  )
}

/** Un porcentaje (en %) como fracción, que es lo que compara `pointsVariation`. */
const toRatio = (percent: number | null): number | null => (percent === null ? null : percent / 100)

/** "1/8–31/8": el período anterior con que se compara; `null` si el backend no lo manda. */
function previousPeriodText(summary: HospitalizationSummaryDTO): string | null {
  if (!summary.previousFrom || !summary.previousTo) return null
  const from = parseIsoDate(summary.previousFrom)
  const to = parseIsoDate(summary.previousTo)
  const withYear = periodNeedsYear(from, to, new Date())
  return `${formatShortDate(from, withYear)}–${formatShortDate(to, withYear)}`
}

interface NoteProps {
  /** La frase ya escrita («205 egresos de menos de 24 h»). */
  text: string
  /** Qué significa y qué valida el servicio, a demanda. */
  hint: string
}

/** Una nota de datos: lo que no entra limpio en los indicadores. Solo se muestra con N > 0 (lo decide quien la arma). */
function Note({ text, hint }: NoteProps) {
  return (
    <p className="nv-note" role="status">
      <i className="pi pi-info-circle" aria-hidden="true" />
      <span>{text}</span>
      <InfoTip text={hint} />
    </p>
  )
}

interface HospitalizationIndicatorsProps {
  summary: HospitalizationSummaryDTO
}

/**
 * Los indicadores del período: ocupación, estadía media, egresos (con las defunciones como renglón) e ingresos, cada
 * uno con su variación contra el período anterior de igual largo, escrita con las fechas de ese período. Todos son neutros,
 * sin «mejor» ni «peor»: la dirección médica todavía no validó qué es mejor en la ocupación ni en la estadía. La fórmula de
 * cada uno está a demanda en su ⓘ (con un filtro de tipo de ingreso o financiador, la ocupación lo aclara ahí). Después, con
 * el mismo formato de tarjeta, el giro cama y el intervalo de sustitución (ocultos si no hay valor; los dos los valida el
 * servicio). Las estadías de
 * menos de 24 h y los datos de camas que no cierran se informan aparte,
 * como nota y solo si hay.
 */
export default function HospitalizationIndicators({ summary }: HospitalizationIndicatorsProps) {
  const intl = useIntl()
  const { current, previous, dataNotes } = summary
  const period = previousPeriodText(summary)

  const line = (variation: Variation | null): ReactNode =>
    (variation && period ? <ChangeLine variation={variation} period={period} /> : undefined)

  const occupancy = pointsVariation(intl, toRatio(current.occupancyPct), toRatio(previous.occupancyPct))
  const stay = current.averageStayDays === null || previous.averageStayDays === null
    ? null : percentVariation(current.averageStayDays, previous.averageStayDays)

  const turnover = current.bedTurnover ?? null
  const interval = current.substitutionIntervalDays ?? null
  const turnoverChange = turnover === null ? null : percentVariation(turnover, previous.bedTurnover)
  const intervalChange = interval === null ? null : percentVariation(interval, previous.substitutionIntervalDays)
  const filteredHint = summary.occupancyOfFilteredPatients
    ? ` ${intl.formatMessage({ id: 'HospitalizationSummaryKpiFilteredHint' })}` : ''

  const count = (id: string, value: number): string =>
    intl.formatMessage({ id }, { count: value, countText: formatCount(intl, value) })

  return (
    <div className="nv-hosp-indicators-box">
      <dl className="nv-hosp-indicators" aria-label={intl.formatMessage({ id: 'HospitalizationSummaryKpisLabel' })}>
        <Indicator
          label={intl.formatMessage({ id: 'HospitalizationSummaryKpiOccupancy' })}
          hint={intl.formatMessage({ id: 'HospitalizationSummaryKpiOccupancyHint' })
            + (summary.occupancyOfFilteredPatients ? ` ${intl.formatMessage({ id: 'HospitalizationSummaryKpiOccupancyFilteredHint' })}` : '')}
          value={formatPercent(intl, current.occupancyPct)}
          details={[
            current.bedDays > 0
              ? intl.formatMessage({ id: 'HospitalizationSummaryOfBedDays' }, {
                count: formatCount(intl, current.patientDays), total: formatCount(intl, current.bedDays),
              })
              : undefined,
            line(occupancy),
          ]}
        />
        <Indicator
          label={intl.formatMessage({ id: 'HospitalizationSummaryKpiAverageStay' })}
          hint={intl.formatMessage({ id: 'HospitalizationSummaryKpiAverageStayHint' })}
          value={current.averageStayDays === null
            ? formatDecimal(intl, null)
            : intl.formatMessage({ id: 'HospitalizationSummaryDaysValue' }, { value: formatDecimal(intl, current.averageStayDays) })}
          details={[line(stay)]}
        />
        <Indicator
          label={intl.formatMessage({ id: 'HospitalizationSummaryKpiDischarges' })}
          hint={intl.formatMessage({ id: 'HospitalizationSummaryKpiDischargesHint' })}
          value={formatCount(intl, current.discharges)}
          details={[
            current.discharges > 0
              ? intl.formatMessage({ id: 'HospitalizationSummaryDeaths' }, {
                count: current.deaths, countText: formatCount(intl, current.deaths), pct: formatPercent(intl, current.deathPct),
              })
              : undefined,
            line(percentVariation(current.discharges, previous.discharges)),
          ]}
        />
        <Indicator
          label={intl.formatMessage({ id: 'HospitalizationSummaryKpiAdmissions' })}
          hint={intl.formatMessage({ id: 'HospitalizationSummaryKpiAdmissionsHint' })}
          value={formatCount(intl, current.admissions)}
          details={[line(percentVariation(current.admissions, previous.admissions))]}
        />
        {turnover !== null && (
          <Indicator
            label={intl.formatMessage({ id: 'HospitalizationSummaryKpiBedTurnover' })}
            hint={intl.formatMessage({ id: 'HospitalizationSummaryKpiBedTurnoverHint' }) + filteredHint}
            value={formatDecimal(intl, turnover)}
            details={[intl.formatMessage({ id: 'HospitalizationSummaryBedTurnoverUnit' }), line(turnoverChange)]}
          />
        )}
        {interval !== null && (
          <Indicator
            label={intl.formatMessage({ id: 'HospitalizationSummaryKpiSubstitution' })}
            hint={intl.formatMessage({ id: 'HospitalizationSummaryKpiSubstitutionHint' }) + filteredHint}
            value={intl.formatMessage({ id: 'HospitalizationSummaryDaysValue' }, { value: formatDecimal(intl, interval) })}
            details={[line(intervalChange)]}
          />
        )}
      </dl>

      {current.shortStays > 0 && (
        <Note
          text={count('HospitalizationSummaryNoteShortStays', current.shortStays)}
          hint={intl.formatMessage({ id: 'HospitalizationSummaryNoteShortStaysHint' })}
        />
      )}
      {dataNotes.bedsWithoutStatus > 0 && (
        <Note
          text={count('HospitalizationSummaryNoteBedsWithoutStatus', dataNotes.bedsWithoutStatus)}
          hint={intl.formatMessage({ id: 'HospitalizationSummaryNoteBedsWithoutStatusHint' })}
        />
      )}
      {dataNotes.duplicatedBeds > 0 && (
        <Note
          text={count('HospitalizationSummaryNoteDuplicatedBeds', dataNotes.duplicatedBeds)}
          hint={intl.formatMessage({ id: 'HospitalizationSummaryNoteDuplicatedBedsHint' })}
        />
      )}
      {dataNotes.internmentsWithoutBed > 0 && (
        <Note
          text={count('HospitalizationSummaryNoteWithoutBed', dataNotes.internmentsWithoutBed)}
          hint={intl.formatMessage({ id: 'HospitalizationSummaryNoteWithoutBedHint' })}
        />
      )}
    </div>
  )
}
